import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { formatApiError } from "../../lib/api";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-homi-black flex items-center justify-center px-6">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-homi-gold/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-homi-gold/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-homi-gold/15 mb-4">
            <ShieldCheck className="w-7 h-7 text-homi-gold" />
          </span>
          <h1 className="font-heading font-extrabold text-2xl text-white">EMB Admin</h1>
          <p className="text-white/50 text-sm mt-1">Sign in to manage your website</p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white rounded-2xl p-8 shadow-2xl">
          {error && (
            <div data-testid="admin-login-error" className="mb-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          <label className="block text-sm font-heading font-semibold text-homi-black mb-2">Email</label>
          <div className="relative mb-4">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-gray-200 rounded-lg pl-11 pr-4 py-3 focus:outline-none focus:border-homi-gold transition-colors"
              required
            />
          </div>
          <label className="block text-sm font-heading font-semibold text-homi-black mb-2">Password</label>
          <div className="relative mb-6">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full border border-gray-200 rounded-lg pl-11 pr-4 py-3 focus:outline-none focus:border-homi-gold transition-colors"
              required
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-homi-gold hover:bg-homi-goldDark text-homi-black font-heading font-bold px-6 py-3.5 rounded-lg transition-colors disabled:opacity-70"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
